import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { healthEncyclopediaApi } from '../api/health-encyclopedia.adapter';
import type { ContentListParams } from '../types/health-encyclopedia.types';

export const healthEncyclopediaQueryKeys = {
  all: ['health-encyclopedia'] as const,
  // The list key carries the full server-side param set so a change to
  // page / size / sort / category / search produces a distinct cache entry.
  list: (params: ContentListParams) =>
    [...healthEncyclopediaQueryKeys.all, 'list', params] as const,
  detail: (eid: string) => [...healthEncyclopediaQueryKeys.all, 'detail', eid] as const,
  categories: () => [...healthEncyclopediaQueryKeys.all, 'categories'] as const,
};

// PRD §3.1–3.3 — server-side list. `keepPreviousData` keeps the current page
// visible while the next page / filter result loads, avoiding an empty flash
// during pagination and search.
export function useContentsQuery(params: ContentListParams) {
  return useQuery({
    queryKey: healthEncyclopediaQueryKeys.list(params),
    queryFn: () => healthEncyclopediaApi.listContents(params),
    placeholderData: keepPreviousData,
  });
}

// PRD §3.6 — detail fetch when the content form opens in edit mode. `eid` is
// null in create mode and the query stays disabled. (Consumed in Slice 5.)
export function useContentDetailQuery(eid: string | null) {
  return useQuery({
    queryKey: healthEncyclopediaQueryKeys.detail(eid ?? ''),
    queryFn: () => healthEncyclopediaApi.getContent(eid as string),
    enabled: eid !== null,
  });
}

// PRD §3.4 — category master + counts for the filter dropdown and the category
// manager. (Consumed in Slices 3–4.)
export function useCategoriesQuery() {
  return useQuery({
    queryKey: healthEncyclopediaQueryKeys.categories(),
    queryFn: () => healthEncyclopediaApi.listCategories(),
  });
}
